import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Grid } from '@mui/material';

import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { ElectRequestDto } from 'enrollment/features/Plans/PlanCard/types/dtos';
import { buildElectionParams } from 'enrollment/features/Plans/PlanCard/utils';
import {
  DeclineModalBodyWrapperStyled,
  PrimaryModalButtonsStyled,
} from './DeclineReasonsModalStyled';
import { LoaderOverlay } from 'enrollment/components/LoaderOverlay';
import { DeclineReasonsModalDropdown } from './DeclineReasonsModalDropdown';

export interface DeclineReasonsModalProps {
  id: string;
  declinePlans?: BenefitPlanDto[];
  orgBenefitId?: number;
  orgBenefitName?: string;
  enrollmentContextType?: string;
  handleDeclineBenefit?: (params: ElectRequestDto) => void;
  modalOpen?: boolean;
  setModalOpen?: (value: boolean) => void;
}

export const DeclineReasonsModal = ({
  id,
  declinePlans,
  orgBenefitId,
  orgBenefitName,
  enrollmentContextType,
  handleDeclineBenefit,
  modalOpen,
  setModalOpen,
}: DeclineReasonsModalProps) => {
  const [selectedReasonLevelId, setSelectedReasonId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation('enrollment.benefits');

  const handleDecline = () => {
    if (selectedReasonLevelId !== 0 && declinePlans && handleDeclineBenefit) {
      setIsLoading(true);
      const plan = declinePlans.find(
        (plan) => plan.level_id === Number(selectedReasonLevelId)
      );
      if (!plan) {
        return;
      }
      handleDeclineBenefit(
        buildElectionParams({
          plan,
          levelId: selectedReasonLevelId,
          orgBenefitId: orgBenefitId,
          enrollmentContextType,
        })
      );
    }
  };

  const handleCloseModal = () => {
    setSelectedReasonId(0);
    setModalOpen?.(false);
  };

  return (
    <Modal
      disableEnforceFocus={true}
      open={Boolean(modalOpen)}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      data-testid={id}
      id={id}
    >
      {isLoading ? (
        <LoaderOverlay />
      ) : (
        <DeclineModalBodyWrapperStyled>
          <DeclineReasonsModalDropdown
            selectedReasonLevelId={selectedReasonLevelId}
            setSelectedReasonId={setSelectedReasonId}
            orgBenefitName={orgBenefitName}
            declinePlans={declinePlans}
            id={`decline-reasons-modal-${selectedReasonLevelId}`}
            orgBenefitId={0}
          />

          <PrimaryModalButtonsStyled container>
            <Grid item>
              <ps-button
                data-testid="cancel-button"
                id="decline-reasons-close-btn"
                level="tertiary"
                label={t('common:cancel')}
                onClick={handleCloseModal}
              />
            </Grid>
            <Grid item>
              <ps-button
                data-testid="confirm-button"
                id="decline-reasons-confirm-btn"
                level="primary"
                label={t('common:confirm')}
                onClick={handleDecline}
                disabled
              />
            </Grid>
          </PrimaryModalButtonsStyled>
        </DeclineModalBodyWrapperStyled>
      )}
    </Modal>
  );
};
